import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Progress,
  Space,
  Spin,
  Tag,
  TimePicker,
  Tooltip,
  message
} from "antd";
import dayjs from "dayjs";
import { fetchHealthClient, postHealthAutoPlan } from "./health-clients-api";
import { pick, toNumber } from "./health-clients-utils";

const DRAWER_WIDTH_KEY = "planOnline.health.clients.autoPlanDrawer.width";
const MIN_WIDTH = 800;
const MAX_WIDTH = 1100;
const DEFAULT_WIDTH = 800;

const clampWidth = (value) => {
  const max = typeof window === "undefined" ? MAX_WIDTH : Math.min(MAX_WIDTH, window.innerWidth);
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_WIDTH;
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

const VID_PAN_LABELS = {
  0: "Не планировать",
  1: "Каждый день",
  2: "Через день",
  7: "Раз в неделю",
  12: "Через день (сквозное)",
  100: "По времени"
};

const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

const toDateKey = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format("YYYY-MM-DD") : null;
};

const toTimeKey = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format("HH:mm") : null;
};

/**
 * Drawer автопланирования (Delphi TFrmAutoPlan).
 */
const ClientAppointmentAutoPlanDrawer = ({
  open,
  idClient,
  appointments,
  selectedIdStrs,
  onClose,
  onDone
}) => {
  const law = useSelector((state) => state.user.session?.law);
  const canOutsideStay = getLawFlag(law, 106) === 1;
  const canUnpaid = getLawFlag(law, 99) === 1;

  const [client, setClient] = useState(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [dateBeg, setDateBeg] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [maxPlan, setMaxPlan] = useState(false);
  const [checked, setChecked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [error, setError] = useState("");
  const [size, setSize] = useState(loadWidth);
  const clientReqRef = useRef(0);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const checklist = useMemo(() => {
    const selected = new Set((selectedIdStrs || []).map((x) => Number(x)));
    return (appointments || []).map((row) => {
      const idStr = toNumber(row.idStr);
      const cntPlan = toNumber(row.cntPlan) ?? 0;
      const cnt = toNumber(row.cnt) ?? 0;
      const vidPan = toNumber(row.vidPan) ?? 0;
      const isPlat = toNumber(row.isPlat) ?? 0;
      const symaPlat = toNumber(row.symaPlat) ?? 0;
      let disabled = cntPlan > 0 || vidPan === 0;
      if (!canUnpaid && isPlat === 1 && symaPlat === 0) disabled = true;
      const vidLabel = VID_PAN_LABELS[vidPan] ?? String(vidPan);
      const pct = cnt > 0 ? Math.min(100, Math.round((cntPlan / cnt) * 100)) : 0;
      const tone = cntPlan <= 0 ? "empty" : cnt > 0 && cntPlan >= cnt ? "full" : "partial";
      return {
        idStr,
        disabled,
        tName: row.tName || "Услуга",
        vidLabel,
        cnt,
        cntPlan,
        pct,
        tone,
        defaultChecked: !disabled && (selected.size === 0 || selected.has(idStr))
      };
    });
  }, [appointments, selectedIdStrs, canUnpaid]);

  useEffect(() => {
    if (!open || !idClient) return;

    const requestId = ++clientReqRef.current;
    setLoadingClient(true);
    setError("");
    setSubmitting(false);
    setProgress(0);
    setProgressLabel("");
    setMaxPlan(false);

    fetchHealthClient(idClient)
      .then((data) => {
        if (requestId !== clientReqRef.current) return;
        const card = {
          name: pick(data, "name", "Name") ?? "",
          dateBeg: pick(data, "dateBeg", "DateBeg"),
          dateEnd: pick(data, "dateEnd", "DateEnd"),
          dateVizit: pick(data, "dateVizit", "DateVizit")
        };
        setClient(card);

        const beg = card.dateBeg ? dayjs(card.dateBeg) : dayjs();
        const end = card.dateEnd ? dayjs(card.dateEnd) : dayjs();
        setDateBeg(beg.isValid() ? beg.startOf("day") : dayjs().startOf("day"));
        setDateEnd(end.isValid() ? end.startOf("day") : dayjs().startOf("day"));

        if (card.dateVizit) {
          const vizit = dayjs(card.dateVizit);
          setStartTime(vizit.isValid() ? vizit.add(1, "hour") : dayjs().add(1, "hour"));
        } else {
          setStartTime(dayjs().add(1, "hour"));
        }
        setEndTime(null);
      })
      .catch((e) => {
        if (requestId !== clientReqRef.current) return;
        setError(e.message || "Не удалось загрузить клиента");
        setClient(null);
      })
      .finally(() => {
        if (requestId === clientReqRef.current) setLoadingClient(false);
      });
  }, [open, idClient]);

  useEffect(() => {
    if (!open) return;
    setChecked(checklist.filter((x) => x.defaultChecked).map((x) => x.idStr));
  }, [open, checklist]);

  const periodAllowed = useMemo(() => {
    if (canOutsideStay) return true;
    if (!client?.dateBeg || !client?.dateEnd || !dateBeg || !dateEnd) return false;
    const cBeg = dayjs(client.dateBeg).startOf("day");
    const cEnd = dayjs(client.dateEnd).startOf("day");
    const beg = dateBeg.startOf("day");
    const end = dateEnd.startOf("day");
    return !beg.isBefore(cBeg) && !end.isAfter(cEnd);
  }, [canOutsideStay, client, dateBeg, dateEnd]);

  const canSubmit = !submitting && !loadingClient && checked.length > 0 && periodAllowed && dateBeg && dateEnd;

  const selectAll = () => {
    setChecked(checklist.filter((x) => !x.disabled).map((x) => x.idStr));
  };

  const clearAll = () => {
    setChecked([]);
  };

  const toggleItem = (idStr, value) => {
    setChecked((prev) => {
      if (value) return prev.includes(idStr) ? prev : [...prev, idStr];
      return prev.filter((x) => x !== idStr);
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const onPlan = () => {
    if (!checked.length) {
      message.error("Нет ни одной процедуры для планирования");
      return;
    }
    if (!periodAllowed) {
      message.error("Период планирования должен быть внутри проживания клиента");
      return;
    }

    setSubmitting(true);
    setError("");
    setProgress(10);
    setProgressLabel("Планирование…");

    const body = {
      dateBeg: toDateKey(dateBeg),
      dateEnd: toDateKey(dateEnd),
      startTime: toTimeKey(startTime),
      endTime: toTimeKey(endTime),
      maxPlan: Boolean(maxPlan),
      items: checked
    };

    postHealthAutoPlan(idClient, body)
      .then((data) => {
        const results = Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.Results)
            ? data.Results
            : [];
        setProgress(100);

        const okCount = results.filter((r) => r.ok ?? r.Ok).length;
        const lines = results.map((r) => {
          const name = pick(r, "tName", "TName") || `#${pick(r, "idStr", "IdStr")}`;
          const planned = toNumber(pick(r, "planned", "Planned")) ?? 0;
          const need = toNumber(pick(r, "need", "Need")) ?? 0;
          const err = pick(r, "error", "Error");
          return err ? `${name}: ${err}` : `${name}: ${planned}/${need}`;
        });

        if (okCount === 0) {
          message.warning(lines.join("; ") || data?.error || data?.Error || "Не удалось запланировать");
        } else if (okCount < results.length) {
          message.info(`Частично: ${lines.join("; ")}`);
        } else {
          message.success(lines.join("; ") || "Запланировано");
        }

        onDone?.();
      })
      .catch((e) => {
        setError(e.message || "Ошибка автопланирования");
      })
      .finally(() => {
        setSubmitting(false);
        setProgressLabel("");
      });
  };

  const footer = (
    <Space style={{ width: "100%", justifyContent: "space-between" }}>
      <Button type="primary" loading={submitting} disabled={!canSubmit} onClick={onPlan}>
        Планировать
      </Button>
      <Button onClick={handleClose} disabled={submitting}>
        Отмена
      </Button>
    </Space>
  );

  const helpTitle = (
    <div className="client-appointment-auto-plan__help">
      <p>
        Автоматически подбирает свободные сеансы для отмеченных назначений с учётом занятости
        клиента, совместимости процедур и вида планирования услуги.
      </p>
      <ul>
        <li>
          <b>Период</b> — даты, в которых ищутся сеансы. Без особого права период должен быть
          внутри проживания клиента.
        </li>
        <li>
          <b>Время (начало / конец)</b> — ограничение на первый и последний день периода: до
          указанного начала и после указанного конца слоты недоступны. Пустое значение — без
          ограничения.
        </li>
        <li>
          <b>Плотное бронирование</b>
          <div className="client-appointment-auto-plan__help-variants">
            <div className="client-appointment-auto-plan__help-variant">
              <Tag color="default">Выкл</Tag>
              <span>лучшая непрерывная серия сеансов (один кабинет/время)</span>
            </div>
            <div className="client-appointment-auto-plan__help-variant">
              <Tag color="processing">Вкл</Tag>
              <span>каждый подходящий день — самый ранний свободный слот</span>
            </div>
          </div>
        </li>
        <li>
          <b>Назначения</b> — какие процедуры планировать. Уже запланированные, «Не планировать»
          и (без права) неоплаченные недоступны для выбора.
        </li>
        <li>
          Режим услуги задаётся в назначении: по дням (каждый день / через день / раз в неделю)
          или по времени (длительность из справочника).
        </li>
      </ul>
    </div>
  );

  const drawerTitle = (
    <div className="client-appointment-auto-plan__title">
      <span className="client-appointment-auto-plan__title-text">
        {client ? `Автоплан — [${idClient}] ${client.name}` : "Автоплан"}
      </span>
    </div>
  );

  const drawerExtra = (
    <Tooltip
      title={helpTitle}
      placement="bottomLeft"
      styles={{ root: { maxWidth: 420 } }}
      classNames={{ root: "client-appointment-auto-plan__help-tooltip" }}
    >
      <Button
        type="text"
        size="small"
        className="client-appointment-auto-plan__info-btn"
        icon={<InfoCircleOutlined />}
        aria-label="Информация об автопланировании"
      />
    </Tooltip>
  );

  return (
    <Drawer
      placement="right"
      size={size}
      maxSize={MAX_WIDTH}
      open={open}
      onClose={handleClose}
      destroyOnClose
      title={drawerTitle}
      extra={drawerExtra}
      rootClassName="client-appointment-auto-plan"
      footer={footer}
      maskClosable={!submitting}
      keyboard={!submitting}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {loadingClient ? (
        <div className="client-appointment-auto-plan__center">
          <Spin tip="Загрузка…" />
        </div>
      ) : (
        <div className="client-appointment-auto-plan__body">
          {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

          <div className="client-appointment-auto-plan__row">
            <label>Период</label>
            <Space wrap>
              <DatePicker
                className="client-appointment-auto-plan__picker"
                value={dateBeg}
                format="DD.MM.YYYY"
                allowClear={false}
                disabled={submitting}
                onChange={setDateBeg}
              />
              <DatePicker
                className="client-appointment-auto-plan__picker"
                value={dateEnd}
                format="DD.MM.YYYY"
                allowClear={false}
                disabled={submitting}
                onChange={setDateEnd}
              />
            </Space>
          </div>

          {!periodAllowed ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Период должен быть внутри проживания клиента"
            />
          ) : null}

          <div className="client-appointment-auto-plan__row">
            <label>Время</label>
            <Space wrap>
              <TimePicker
                className="client-appointment-auto-plan__picker"
                value={startTime}
                format="HH:mm"
                minuteStep={5}
                disabled={submitting}
                onChange={setStartTime}
                placeholder="Начало"
                allowClear
              />
              <TimePicker
                className="client-appointment-auto-plan__picker"
                value={endTime}
                format="HH:mm"
                minuteStep={5}
                disabled={submitting}
                onChange={setEndTime}
                placeholder="Конец"
                allowClear
              />
            </Space>
          </div>

          <div className="client-appointment-auto-plan__row client-appointment-auto-plan__row--nowrap">
            <Checkbox checked={maxPlan} disabled={submitting} onChange={(e) => setMaxPlan(e.target.checked)}>
              Плотное бронирование
            </Checkbox>
          </div>

          <div className="client-appointment-auto-plan__list-head">
            <span>Назначения</span>
            <Space size={4}>
              <Button size="small" disabled={submitting} onClick={selectAll}>
                Все
              </Button>
              <Button size="small" disabled={submitting} onClick={clearAll}>
                Снять
              </Button>
            </Space>
          </div>

          <ul className="client-appointment-auto-plan__list">
            {checklist.map((item) => (
              <li key={item.idStr} className="client-appointment-auto-plan__item">
                <Checkbox
                  checked={checked.includes(item.idStr)}
                  disabled={item.disabled || submitting}
                  onChange={(e) => toggleItem(item.idStr, e.target.checked)}
                >
                  <span className="client-appointment-auto-plan__item-name">{item.tName}</span>
                  <span className="client-appointment-auto-plan__item-vid">{item.vidLabel}</span>
                </Checkbox>
                <div
                  className={`client-appointment-auto-plan__item-progress client-appointment-auto-plan__item-progress--${item.tone}`}
                >
                  <div className="client-appointment-auto-plan__item-progress-track">
                    <div
                      className="client-appointment-auto-plan__item-progress-fill"
                      style={{ width: `${item.tone === "empty" ? 100 : item.pct}%` }}
                    />
                  </div>
                  <div className="client-appointment-auto-plan__item-progress-text">
                    Запланировано {item.cntPlan} из {item.cnt}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {submitting || progress > 0 ? (
            <div className="client-appointment-auto-plan__progress">
              <Progress percent={progress} size="small" status={submitting ? "active" : "normal"} />
              {progressLabel ? <div className="client-appointment-auto-plan__progress-label">{progressLabel}</div> : null}
            </div>
          ) : null}
        </div>
      )}
    </Drawer>
  );
};

export default ClientAppointmentAutoPlanDrawer;
